import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CallingService } from '../calling.service';

export interface CentroDispositionOption {
  id: string;
  reason: string;
  label: string;
}

export interface CentroDisposeContext {
  recordId?: string;
  crmDataId?: string;
  crmLeadId?: string;
  phoneNo?: string;
  callType?: string;
}

@Injectable({ providedIn: 'root' })
export class CentroWrapUpService {
  constructor(private callingService: CallingService) {}

  /**
   * Map a CRM timeline response title (e.g. "Not Connected") onto Centro campaign reasons
   * (lowercase, often "not connected"). Exact match first, then compact (ignore spaces/_/-).
   * If nothing matches, return the lowercased CRM text so Centro still receives the same disposition.
   */
  matchDisposition(raw: string, options: CentroDispositionOption[] = []): string {
    const text = String(raw || '').trim();
    if (!text) return '';
    const lower = text.toLowerCase();
    const compact = lower.replace(/[\s/_-]+/g, '');
    const exact = options.find(
      (item) => item.reason === lower || String(item.label || '').toLowerCase() === lower
    );
    if (exact?.reason) return exact.reason;
    const fuzzy = options.find((item) => {
      const reasonKey = String(item.reason || '').replace(/[\s/_-]+/g, '');
      const labelKey = String(item.label || '').toLowerCase().replace(/[\s/_-]+/g, '');
      return reasonKey === compact || labelKey === compact;
    });
    return fuzzy?.reason || lower;
  }

  mapDispositions(raw: unknown): CentroDispositionOption[] {
    const list = Array.isArray(raw) ? raw : [];
    return list
      .map((item) => {
        const text = String(item || '').trim();
        if (!text) return '';
        const reason = text.includes('~') ? text.split('~')[0].trim() : text;
        return reason.toLowerCase();
      })
      .filter(Boolean)
      .map((reason) => ({
        id: reason,
        reason,
        label: reason.replace(/__/g, ' / '),
      }));
  }

  async loadDispositions(): Promise<CentroDispositionOption[]> {
    const res: any = await firstValueFrom(this.callingService.centroGetDispositions());
    if (res?.resultShort === 'success') {
      return this.mapDispositions(res.dispositions);
    }
    return [];
  }

  async submitDisposition(
    disposition: string,
    remarks?: string,
    context: CentroDisposeContext = {}
  ): Promise<{ success: boolean; message?: string }> {
    const reason = String(disposition || '').trim();
    if (!reason) return { success: false, message: 'Select a disposition' };
    const payload = {
      disposition: reason,
      remarks: remarks?.trim() || '',
      recordId: String(context.recordId || '').trim(),
      crmDataId: String(context.crmDataId || '').trim(),
      crmLeadId: String(context.crmLeadId || '').trim(),
      phoneNo: String(context.phoneNo || '').replace(/\D/g, '').slice(-10),
      callType: String(context.callType || '').trim(),
    };
    console.log('[CentroWrapUp] submitDisposition', payload);
    const res: any = await firstValueFrom(this.callingService.centroDisposeCall(payload));
    if (res?.resultShort === 'success') {
      console.log('[CentroWrapUp] submitDisposition:success');
      return { success: true };
    }
    console.warn('[CentroWrapUp] submitDisposition:failed', res?.resultLong || '');
    return { success: false, message: res?.resultLong || 'Failed to dispose call' };
  }
}
